import hyphenateStyleName from 'hyphenate-style-name';
import * as React from 'react';

export type ViewStyle = Omit<React.CSSProperties, 'transform'> & {
    [property: string]: any;
    animationPlayState?: 'paused' | 'running';
    marginHorizontal?: number | string;
    marginVertical?: number | string;
    paddingHorizontal?: number | string;
    paddingVertical?: number | string;
    pointerEvents?: 'auto' | 'none';
    shadowColor?: string;
    shadowOffset?: { height?: number; width?: number };
    shadowOpacity?: number;
    shadowRadius?: number;
    textDecorationLine?: string;
    transform?: string | Array<Record<string, number | string>>;
};

export type TextStyle = ViewStyle;
export type ImageStyle = ViewStyle;

export type StyleProp<T> =
    | T
    | Record<string, any>
    | ReadonlyArray<StyleProp<T>>
    | null
    | undefined
    | false;

export interface LayoutRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export type GestureResponderEvent = React.MouseEvent<HTMLElement>;

export type ViewProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> & {
    children?: React.ReactNode;
    accessibilityChecked?: boolean;
    accessibilityHidden?: boolean;
    accessibilityLabel?: string;
    accessibilityLabelledBy?: string;
    accessibilityRole?: string;
    dataSet?: Record<string, string>;
    editable?: boolean;
    nativeID?: string;
    pointerEvents?: 'auto' | 'none';
    style?: StyleProp<ViewStyle>;
    testID?: string;
    translate?: boolean;
};

export type TextProps = Omit<React.HTMLAttributes<HTMLElement>, 'style'> & {
    children?: React.ReactNode;
    accessibilityLabel?: string;
    accessibilityRole?: string;
    dataSet?: Record<string, string>;
    numberOfLines?: number;
    onPress?: (event: GestureResponderEvent) => void;
    onPressIn?: (event: GestureResponderEvent) => void;
    onPressOut?: (event: GestureResponderEvent) => void;
    selectable?: boolean;
    style?: StyleProp<TextStyle>;
    testID?: string;
};

export const StyleSheet = {
    absoluteFill: {
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
    } satisfies ViewStyle,
    absoluteFillObject: {
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
    } satisfies ViewStyle,
    create<T extends Record<string, any>>(styles: T): T {
        return styles;
    },
    flatten<T>(style: StyleProp<T>): T | undefined {
        return flattenStyle(style);
    },
    hairlineWidth: 1,
};

export function flattenStyle<T>(style: StyleProp<T>): T | undefined {
    if (!Array.isArray(style)) {
        return (style || undefined) as T | undefined;
    }

    return style.reduce<T | undefined>((result, item) => {
        const flattened = flattenStyle(item as StyleProp<T>);
        return flattened ? { ...result, ...flattened } : result;
    }, undefined);
}

export function normalizeStyle(style: StyleProp<ViewStyle>): React.CSSProperties | undefined {
    const flattened = flattenStyle(style);
    if (!flattened) {
        return undefined;
    }

    const result: Record<string, any> = {};
    for (const [property, value] of Object.entries(flattened as Record<string, any>)) {
        if (value === undefined || value === null || value === false) {
            continue;
        }

        switch (property) {
            case 'marginHorizontal':
                result.marginLeft = value;
                result.marginRight = value;
                break;
            case 'marginVertical':
                result.marginBottom = value;
                result.marginTop = value;
                break;
            case 'paddingHorizontal':
                result.paddingLeft = value;
                result.paddingRight = value;
                break;
            case 'paddingVertical':
                result.paddingBottom = value;
                result.paddingTop = value;
                break;
            case 'pointerEvents':
                result.pointerEvents = value;
                break;
            case 'textDecorationLine':
                result.textDecoration = value;
                break;
            case 'lineHeight':
                result.lineHeight = typeof value === 'number' ? `${value}px` : value;
                break;
            case 'transform':
                result.transform = normalizeTransform(value);
                break;
            case 'shadowColor':
            case 'shadowOffset':
            case 'shadowOpacity':
            case 'shadowRadius':
                break;
            default:
                result[property] = value;
                break;
        }
    }

    if (
        flattened.shadowColor ||
        flattened.shadowOffset ||
        flattened.shadowOpacity ||
        flattened.shadowRadius
    ) {
        const offset = flattened.shadowOffset || {};
        const opacity = flattened.shadowOpacity ?? 1;
        const color = flattened.shadowColor || 'black';
        result.boxShadow = `${offset.width || 0}px ${offset.height || 0}px ${flattened.shadowRadius || 0}px ${colorWithOpacity(color, opacity)}`;
    }

    return result as React.CSSProperties;
}

export function styleToCSS(style: StyleProp<ViewStyle>): string {
    const normalized = normalizeStyle(style);
    if (!normalized) {
        return '';
    }

    return Object.entries(normalized)
        .map(([property, value]) => `${hyphenateStyleName(property)}:${cssValue(property, value)};`)
        .join('');
}

function cssValue(property: string, value: any): string | number {
    if (typeof value !== 'number' || value === 0 || UNIT_LESS_PROPERTIES.has(property)) {
        return typeof value === 'string' ? value : String(value);
    }
    return `${value}px`;
}

function normalizeTransform(transform: any): string {
    if (!Array.isArray(transform)) {
        return String(transform);
    }

    return transform
        .map((item) => {
            const [name, value] = Object.entries(item)[0] || [];
            if (!name) {
                return '';
            }
            const unit =
                name === 'rotate' || name === 'rotateX' || name === 'rotateY' || name === 'rotateZ'
                    ? 'deg'
                    : name === 'translateX' || name === 'translateY' || name === 'translateZ'
                      ? 'px'
                      : '';
            return `${name}(${value}${typeof value === 'number' ? unit : ''})`;
        })
        .filter(Boolean)
        .join(' ');
}

function colorWithOpacity(color: string, opacity: number): string {
    if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
    }
    if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
        const value =
            color.length === 4
                ? color
                      .slice(1)
                      .split('')
                      .map((part) => part + part)
                      .join('')
                : color.slice(1);
        const channels = [0, 2, 4].map((offset) =>
            Number.parseInt(value.slice(offset, offset + 2), 16)
        );
        return `rgba(${channels.join(', ')}, ${opacity})`;
    }
    return color;
}

const UNIT_LESS_PROPERTIES = new Set([
    'animationIterationCount',
    'columnCount',
    'flex',
    'flexGrow',
    'flexShrink',
    'fontWeight',
    'lineHeight',
    'opacity',
    'order',
    'zIndex',
]);
